// src/components/about/AboutUs.jsx
import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import AOS from "aos";
import "aos/dist/aos.css";
import "./about.css";
import Footer from "../footer/footer";
import {
  FaGlobe,
  FaLeaf,
  FaGem,
  FaHandsHelping,
  FaChartLine,
} from "react-icons/fa";

const AboutUs = () => {
  const [currentTagline, setCurrentTagline] = useState(0);

  const taglines = [
    "Beyond Basic Linen. Beyond Expectation.",
    "Linen That Performs. Linen That Defines Style.",
    "Linen’s Next Chapter. Written by Lismount.",
    "Linen Born of Heritage, Perfected by Research.",
    "Linen That Leads. Crafted by Lismount.",
    "Linen Defined by Our Past, Woven for Your Future.",
    "Lismount: Simply Better Linen.",
    "The Luxury of Linen. The Assurance of Performance.",
    "Linen That Works As Hard As It Looks.",
  ];

  const coreValues = [
    {
      icon: <FaGem />,
      title: "Quality First",
      desc: "We set the highest standards and apply them to every part of our production process.",
    },
    {
      icon: <FaHandsHelping />,
      title: "Respect for Heritage",
      desc: "We merge centuries of craft knowledge with modern workflows for unmatched linen quality.",
    },
    {
      icon: <FaChartLine />,
      title: "Never Stop Improving",
      desc: "Dedicated R&D ensures we keep pushing linen's performance and possibilities.",
    },
    {
      icon: <FaLeaf />,
      title: "Sustainability",
      desc: "Eco-aware sourcing and processes—luxury fabric produced responsibly.",
    },
    {
      icon: <FaGlobe />,
      title: "Expert Team",
      desc: "Globally-trained artisans and specialists drive our production excellence.",
    },
  ];

  // images to use on page (9 images). Put these files in public/files/
  const images = [
    { src: "/files/DSC06908 E.jpg", caption: "Final fabric rolls ready for dispatch" },
    { src: "/files/DSC06961 E (1).jpg", caption: "Quality fabric inspection at finishing" },
    { src: "/files/DSC06916 E.jpg", caption: "In-house loom & weaving lines" },
    { src: "/files/DSC06959 E.jpg", caption: "Close up: warp threads & tooling" },
    { src: "/files/DSC06881 E.jpg", caption: "Thread detail and yarn control" },
    { src: "/files/DSC06891 E.jpg", caption: "Looms in action — production floor" },
    { src: "/files/DSC06900 E.jpg", caption: "Precision finishing & checks" },
    { src: "/files/DSC06635.jpg", caption: "Skilled operator checking fabric" },
    { src: "/files/DSC06910 E.jpg", caption: "Precision meters and rolling" },
    { src: "/files/DSC06566.jpg", caption: "Crafting precision in every weave" },
    { src: "/files/DSC06572.jpg", caption: "Skill, dedication, craftsmanship" },
    { src: "/files/DSC06643.jpg", caption: "Accuracy in every meter" },
    { src: "/files/DSC06739.jpg", caption: "Perfectly prepared warp lines" },
    { src: "/files/DSC068091.jpg", caption: "Patterns woven to perfection" },
    { src: "/files/DSC06814.jpg", caption: "Engineered for smooth production" },
    { src: "/files/DSC06917.jpg", caption: "Strong threads. Strong fabric" },
    { src: "/files/DSC06970.jpg", caption: "Quality rolled, ready to deliver" },

  ];

  useEffect(() => {
    AOS.init({ duration: 800, once: true });

    const interval = setInterval(() => {
      setCurrentTagline((prev) => (prev + 1) % taglines.length);
    }, 3000);

    return () => clearInterval(interval);
  }, [taglines.length]);

  return (
    <div className="about-page">
      {/* HERO */}
      <section
        className="about-hero"
        style={{
          backgroundImage: `url('/files/DSC06915.JPG')`,
        }}
      >
        <div className="hero-overlay" />
        <div className="hero-content" data-aos="fade-up">
          <h1 className="hero-title">About Lismount Tex India LLP</h1>
          <p className="hero-subtitle">
            Blending Irish heritage with Indian innovation to craft the future of linen.
          </p>
        </div>
      </section>

      {/* STORY */}
      <section className="about-header" data-aos="fade-up">
        <div className="about-flex">
          <div className="about-text">
            <h2 className="underline-title">The Lismount Story</h2>
            <p>
              Lismount Tex India LLP launched with a bold mission: to create the next generation of
              premium linen by combining long-form craftsmanship with modern textile science.
            </p>
            <p>
              Trained with Irish masters and powered by continuous R&D, our team pushes linen beyond
              what traditional fabrics can do — delivering elegant, high-performance materials
              for brands worldwide.
            </p>
            <p className="small-note">
              <strong>Headquarters:</strong> India • <strong>Focus:</strong> Sustainable, traceable linen
            </p>
          </div>

          <div className="about-image">
            <img src="/files/DSC06539.JPG" alt="Lismount Fabrics" />
            <p className="image-caption">Our sample lab — quality checks and color matching</p>
          </div>
        </div>
      </section>

      {/* ADVANTAGE */}
      <section className="about-advantage" data-aos="fade-up">
        <div className="advantage-content">
          <div className="advantage-image">
            <img src="/files/DSC06967.jpg" alt="Irish Collaboration" />
            <p className="image-caption">Direct collaboration with Irish linen experts</p>
          </div>
          <div className="advantage-text">
            <h3>The Lismount Advantage</h3>
            <p>
              We combine historic linen knowledge with modern innovation. Our artisans work on advanced
              looms and finishing lines, producing linen that is luxurious, durable, and ready for
              contemporary design applications.
            </p>
            <ul className="adv-list">
              <li>Specialized yarn sourcing & traceability</li>
              <li>Precise color matching & low-impact dyeing</li>
              <li>Scalable production without compromising quality</li>
            </ul>
          </div>
        </div>
      </section>

      {/* VISION / MISSION */}
      <section className="vision-mission-section" data-aos="fade-up">
        <div className="vision-card">
          <h3>Our Vision</h3>
          <p>
            To be a world leader in sustainable, high-performance linen — combining Irish expertise
            with Indian craft.
          </p>
        </div>
        <div className="mission-card">
          <h3>Our Mission</h3>
          <p>
            To create durable, beautiful linen with consistent quality through continuous R&D and
            responsible manufacturing practices.
          </p>
        </div>
      </section>

      {/* CORE VALUES */}
      <section className="core-values" data-aos="fade-up">
        <h3 className="underline-title">Core Values</h3>
        <div className="values-row">
          {coreValues.map((cv, i) => (
            <motion.div
              key={i}
              className="value-card"
              whileHover={{ y: -8, scale: 1.02 }}
              transition={{ duration: 0.25 }}
            >
              <div className="value-icon">{cv.icon}</div>
              <h5>{cv.title}</h5>
              <p>{cv.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* TAGLINES */}
      <section className="taglines-section" data-aos="fade-up">
        <h3 className="underline-title">Our Taglines</h3>
        <div className="tagline-display">
          <AnimatePresence mode="wait">
            <motion.h4
              key={currentTagline}
              className="tagline-text"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.6 }}
            >
              {taglines[currentTagline]}
            </motion.h4>
          </AnimatePresence>
        </div>
      </section>

      {/* ===== FABRIC SHOWCASE - AUTO SCROLL MARQUEE ===== */}
      <section className="fabric-showcase-section" data-aos="fade-up">
        <div className="section-inner">
          <h3 className="underline-title">Fabric Showcase</h3>
          <p className="lead-text small">
            Real production shots from our manufacturing units — inspection, warp setup, weaving and finishing.
          </p>

          <div className="marquee-wrap" aria-hidden="false">
            <div className="marquee">
              {/* duplicate list for seamless loop */}
              {[...images, ...images].map((img, idx) => (
                <figure key={idx} className="marquee-item">
                  <img src={img.src} alt={img.caption} loading="lazy" />
                  <figcaption>{img.caption}</figcaption>
                </figure>
              ))}
            </div>
          </div>

          {/* <p className="marquee-note">
            Hover slider to pause • click image name to view in larger modal (future enhancement)
          </p> */}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutUs;
